import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Shield, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { STATUS_LABEL, STATUS_TONE } from "../lib/businessSearch";
import { ExternalLink } from "lucide-react";

export default function AdminPanel() {
  const { user } = useAuth();
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loadingJ, setLoadingJ] = useState(true);
  const [verifying, setVerifying] = useState(null);
  const [feeWaivers, setFeeWaivers] = useState([]);
  const [loadingFW, setLoadingFW] = useState(true);
  const [verifyingFW, setVerifyingFW] = useState(null);
  const [runningHealth, setRunningHealth] = useState(false);
  const [healthEvents, setHealthEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/lawyers");
      setLawyers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load lawyers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_admin) { load(); loadJurisdictions(); loadFeeWaivers(); loadHealthEvents(); } }, [user]);

  const loadJurisdictions = async () => {
    setLoadingJ(true);
    try {
      const r = await api.get("/jurisdictions");
      setJurisdictions(r.data);
    } catch (e) {
      toast.error("Could not load jurisdictions");
    } finally {
      setLoadingJ(false);
    }
  };

  const loadFeeWaivers = async () => {
    setLoadingFW(true);
    try {
      const r = await api.get("/fee-waivers");
      setFeeWaivers(r.data);
    } catch (e) {
      toast.error("Could not load fee waivers");
    } finally {
      setLoadingFW(false);
    }
  };

  const verifyJurisdiction = async (code, status) => {
    setVerifying(code + status);
    try {
      const r = await api.post(`/admin/jurisdictions/${code}/verify`, { status });
      setJurisdictions((prev) => prev.map((j) => (j.state_code === code ? r.data : j)));
      toast.success(`${code} marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally {
      setVerifying(null);
    }
  };

  const verifyFeeWaiver = async (code, status) => {
    setVerifyingFW(code + status);
    try {
      const r = await api.post(`/admin/fee-waivers/${code}/verify`, { status });
      setFeeWaivers((prev) => prev.map((f) => (f.code === code ? r.data : f)));
      toast.success(`${code} marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally {
      setVerifyingFW(null);
    }
  };

  const runLinkHealth = async () => {
    if (!window.confirm("Run link-health check on all jurisdiction + fee-waiver URLs now? This may take a minute.")) return;
    setRunningHealth(true);
    try {
      const r = await api.post("/admin/link-health/run");
      toast.success(`Checked ${r.data.checked} URLs — ${r.data.updated} status updates`);
      loadJurisdictions();
      loadFeeWaivers();
      loadHealthEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link-health run failed");
    } finally {
      setRunningHealth(false);
    }
  };

  const loadHealthEvents = async () => {
    setLoadingEvents(true);
    try {
      const r = await api.get("/admin/link-health/events", { params: { limit: 10 } });
      setHealthEvents(r.data);
    } catch (e) {
      // silent; log tab is optional
    } finally {
      setLoadingEvents(false);
    }
  };

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/lawyers/${id}/${action}`);
      toast.success(`Lawyer ${action}d`);
      load();
    } catch (e) {
      toast.error("Action failed");
    } finally {
      setBusy(null);
    }
  };

  if (!user) return null;

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <div className="border border-red-200 bg-red-50 p-8 text-red-900" data-testid="admin-denied">
            <Shield weight="duotone" className="w-10 h-10" />
            <h2 className="font-serif-display text-2xl mt-3">Admin access required</h2>
            <p className="mt-2 text-sm">Your account is not on the admin allowlist. Contact corporate@mishertech.com to request access.</p>
            <div className="mt-4">
              <Link to="/dashboard" className="underline text-sm">Back to dashboard</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pending = lawyers.filter((l) => l.lawyer_status === "pending");
  const approved = lawyers.filter((l) => l.lawyer_status === "approved");
  const rejected = lawyers.filter((l) => l.lawyer_status === "rejected");

  const Card = ({ l }) => {
    const lp = l.lawyer_profile || {};
    const cls = l.lawyer_status === "approved" ? "text-emerald-700" : l.lawyer_status === "rejected" ? "text-red-700" : "text-amber-700";
    const Icon = l.lawyer_status === "approved" ? CheckCircle : l.lawyer_status === "rejected" ? XCircle : Clock;
    return (
      <div className="border border-slate-200 -mt-px p-5" data-testid={`admin-lawyer-${l.user_id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="font-serif-display text-xl text-slate-900">{l.name}</div>
            <div className="text-xs text-slate-500">{l.email}</div>
            <div className="mt-2 text-sm text-slate-700">
              <span className="label-tiny">Firm:</span> {lp.firm_name || "—"} · <span className="label-tiny">Bar:</span> {lp.bar_state} {lp.bar_number}
            </div>
            {lp.practice_areas?.length > 0 && (
              <div className="mt-2 flex gap-1 flex-wrap">
                {lp.practice_areas.map((pa) => <Badge key={pa} variant="outline" className="rounded-none border-slate-300 text-[10px]">{pa}</Badge>)}
              </div>
            )}
            {lp.bio && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{lp.bio}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`flex items-center gap-1 label-tiny ${cls}`}>
              <Icon className="w-4 h-4" /> {l.lawyer_status}
            </div>
            {l.lawyer_status !== "approved" && (
              <Button size="sm" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" disabled={busy === l.user_id + "approve"} onClick={() => act(l.user_id, "approve")} data-testid={`approve-${l.user_id}`}>
                Approve
              </Button>
            )}
            {l.lawyer_status !== "rejected" && (
              <Button size="sm" variant="outline" className="rounded-none border-slate-300" disabled={busy === l.user_id + "reject"} onClick={() => act(l.user_id, "reject")} data-testid={`reject-${l.user_id}`}>
                Reject
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[1200px] mx-auto px-6 py-10" data-testid="admin-panel">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <div className="label-tiny mb-2">Admin</div>
            <h1 className="font-serif-display text-4xl text-slate-900 leading-tight">
              <Shield weight="duotone" className="inline-block w-10 h-10 mr-2 -mt-2" /> Admin console
            </h1>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-600 underline">Back to dashboard</Link>
        </div>

        {/* Anchor-nav hub — quick jump to each admin section */}
        <nav className="mb-10 flex flex-wrap gap-2 text-xs font-mono-tech uppercase tracking-wider" data-testid="admin-hub-nav">
          {[
            ["Lawyers", "#lawyers"],
            ["Business Search", "#admin-jurisdictions-section"],
            ["Fee Waivers", "#admin-fee-waivers-section"],
            ["Link Health", "#admin-link-health-log-section"],
            ["Account Deletions", "#admin-deletion-audits-section"],
            ["Retention Reminders", "#admin-retention-audits-section"],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="border border-slate-300 px-3 py-1.5 hover:bg-slate-50 text-slate-700 hover:text-slate-900"
              data-testid={`admin-hub-link-${label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {label}
            </a>
          ))}
        </nav>

        {loading ? (
          <div className="border border-slate-200 p-8 label-tiny">Loading…</div>
        ) : (
          <>
            <section id="lawyers">
              <h2 className="font-serif-display text-2xl mb-3">Pending <span className="label-tiny ml-2">{pending.length}</span></h2>
              {pending.length === 0 ? <div className="border border-slate-200 p-6 text-slate-500">No pending applications.</div> : pending.map((l) => <Card key={l.user_id} l={l} />)}
            </section>
            <section className="mt-10">
              <h2 className="font-serif-display text-2xl mb-3">Approved <span className="label-tiny ml-2">{approved.length}</span></h2>
              {approved.length === 0 ? <div className="border border-slate-200 p-6 text-slate-500">No approved lawyers yet.</div> : approved.map((l) => <Card key={l.user_id} l={l} />)}
            </section>
            {rejected.length > 0 && (
              <section className="mt-10">
                <h2 className="font-serif-display text-2xl mb-3">Rejected <span className="label-tiny ml-2">{rejected.length}</span></h2>
                {rejected.map((l) => <Card key={l.user_id} l={l} />)}
              </section>
            )}

            {/* Business-search jurisdictions */}
            <section className="mt-14" data-testid="admin-jurisdictions-section">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <h2 className="font-serif-display text-2xl">
                  Business-search jurisdictions
                  <span className="label-tiny ml-2">{jurisdictions.length}</span>
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    size="sm"
                    onClick={runLinkHealth}
                    disabled={runningHealth}
                    className="rounded-none bg-slate-900 hover:bg-slate-800 text-white text-xs h-8"
                    data-testid="run-link-health-btn"
                  >
                    {runningHealth ? "Running…" : "Run link-health check now"}
                  </Button>
                  <span className="text-xs text-slate-500">
                    Nightly automated check runs at 03:00 UTC.
                  </span>
                </div>
              </div>
              {loadingJ ? (
                <div className="border border-slate-200 p-6 label-tiny">Loading…</div>
              ) : (
                <div className="border border-slate-200">
                  <div className="grid grid-cols-12 border-b border-slate-200 bg-slate-50 px-4 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-slate-600">
                    <div className="col-span-3">Jurisdiction</div>
                    <div className="col-span-4">Agency</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-3 text-right">Actions</div>
                  </div>
                  {jurisdictions.map((j) => (
                    <div
                      key={j.state_code}
                      className="grid grid-cols-12 border-b border-slate-200 last:border-b-0 px-4 py-3 items-center text-sm"
                      data-testid={`admin-jurisdiction-${j.state_code}`}
                    >
                      <div className="col-span-3">
                        <div className="font-mono-tech text-[10px] text-slate-500 uppercase tracking-widest">{j.state_code}</div>
                        <div className="font-medium text-slate-900">{j.state_name}</div>
                      </div>
                      <div className="col-span-4 text-slate-700 text-xs pr-3">
                        <div className="truncate">{j.agency}</div>
                        <a href={j.search_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-4 mt-1" data-testid={`admin-open-search-${j.state_code}`}>
                          Open search <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-block border px-2 py-0.5 font-mono-tech text-[10px] uppercase tracking-wider ${STATUS_TONE[j.status] || STATUS_TONE.pending_review}`}>
                          {STATUS_LABEL[j.status] || j.status}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-1 tabular-nums">
                          {j.last_verified_at || "not verified"}
                        </div>
                      </div>
                      <div className="col-span-3 flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={verifying === j.state_code + "active"}
                          onClick={() => verifyJurisdiction(j.state_code, "active")}
                          className="rounded-none border-slate-300 text-[11px] h-7 px-2"
                          data-testid={`verify-active-${j.state_code}`}
                        >
                          Mark active
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={verifying === j.state_code + "redirected"}
                          onClick={() => verifyJurisdiction(j.state_code, "redirected")}
                          className="rounded-none border-amber-300 text-amber-800 text-[11px] h-7 px-2"
                          data-testid={`verify-redirected-${j.state_code}`}
                        >
                          Redirected
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={verifying === j.state_code + "broken"}
                          onClick={() => verifyJurisdiction(j.state_code, "broken")}
                          className="rounded-none border-red-300 text-red-800 text-[11px] h-7 px-2"
                          data-testid={`verify-broken-${j.state_code}`}
                        >
                          Broken
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Fee waivers */}
            <section className="mt-14" data-testid="admin-fee-waivers-section">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <h2 className="font-serif-display text-2xl">
                  Court fee waivers
                  <span className="label-tiny ml-2">{feeWaivers.length}</span>
                </h2>
                <span className="text-xs text-slate-500">
                  Use verify buttons after a manual link check. Nightly automated check also runs at 03:00 UTC.
                </span>
              </div>
              {loadingFW ? (
                <div className="border border-slate-200 p-6 label-tiny">Loading…</div>
              ) : (
                <div className="border border-slate-200">
                  <div className="grid grid-cols-12 border-b border-slate-200 bg-slate-50 px-4 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-slate-600">
                    <div className="col-span-3">Jurisdiction</div>
                    <div className="col-span-4">Program</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-3 text-right">Actions</div>
                  </div>
                  {feeWaivers.map((f) => (
                    <div key={f.code} className="grid grid-cols-12 border-b border-slate-200 last:border-b-0 px-4 py-3 items-center text-sm" data-testid={`admin-fee-waiver-${f.code}`}>
                      <div className="col-span-3">
                        <div className="font-mono-tech text-[10px] text-slate-500 uppercase tracking-widest">{f.code}{f.scope === "federal" ? " · federal" : ""}</div>
                        <div className="font-medium text-slate-900">{f.jurisdiction_name}</div>
                      </div>
                      <div className="col-span-4 text-slate-700 text-xs pr-3">
                        <div className="truncate">{f.program_name}</div>
                        <a href={f.official_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-4 mt-1" data-testid={`admin-open-fw-${f.code}`}>
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-block border px-2 py-0.5 font-mono-tech text-[10px] uppercase tracking-wider ${STATUS_TONE[f.status] || STATUS_TONE.pending_review}`}>
                          {STATUS_LABEL[f.status] || f.status}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-1 tabular-nums">
                          {f.last_verified_at || "not verified"}
                        </div>
                      </div>
                      <div className="col-span-3 flex flex-wrap justify-end gap-1">
                        <Button size="sm" variant="outline" disabled={verifyingFW === f.code + "active"} onClick={() => verifyFeeWaiver(f.code, "active")} className="rounded-none border-slate-300 text-[11px] h-7 px-2" data-testid={`verify-fw-active-${f.code}`}>
                          Mark active
                        </Button>
                        <Button size="sm" variant="outline" disabled={verifyingFW === f.code + "redirected"} onClick={() => verifyFeeWaiver(f.code, "redirected")} className="rounded-none border-amber-300 text-amber-800 text-[11px] h-7 px-2" data-testid={`verify-fw-redirected-${f.code}`}>
                          Redirected
                        </Button>
                        <Button size="sm" variant="outline" disabled={verifyingFW === f.code + "broken"} onClick={() => verifyFeeWaiver(f.code, "broken")} className="rounded-none border-red-300 text-red-800 text-[11px] h-7 px-2" data-testid={`verify-fw-broken-${f.code}`}>
                          Broken
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Link-Health event log */}
            <section className="mt-14" data-testid="admin-link-health-log-section">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <h2 className="font-serif-display text-2xl">
                  Link-health runs
                  <span className="label-tiny ml-2">{healthEvents.length}</span>
                </h2>
                <span className="text-xs text-slate-500">
                  Last 10 runs. Automated runs are labeled "automated".
                </span>
              </div>
              {loadingEvents ? (
                <div className="border border-slate-200 p-6 label-tiny">Loading…</div>
              ) : healthEvents.length === 0 ? (
                <div className="border border-dashed border-slate-300 p-6 text-slate-500 text-sm" data-testid="link-health-empty">
                  No link-health runs recorded yet. Trigger one manually or wait for the nightly job at 03:00 UTC.
                </div>
              ) : (
                <div className="border border-slate-200">
                  <div className="grid grid-cols-12 border-b border-slate-200 bg-slate-50 px-4 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-slate-600">
                    <div className="col-span-3">Run at</div>
                    <div className="col-span-2">Collection</div>
                    <div className="col-span-2 text-right">Checked</div>
                    <div className="col-span-2 text-right">Updated</div>
                    <div className="col-span-3">Flipped URLs</div>
                  </div>
                  {healthEvents.map((ev, i) => {
                    const flipped = (ev.items || []).filter((x) => x.new_status !== "active");
                    return (
                      <details
                        key={i}
                        className="border-b border-slate-200 last:border-b-0"
                        data-testid={`link-health-event-${i}`}
                      >
                        <summary className="grid grid-cols-12 px-4 py-3 items-center text-sm cursor-pointer hover:bg-slate-50">
                          <div className="col-span-3 tabular-nums text-slate-800">{new Date(ev.run_at).toLocaleString()}</div>
                          <div className="col-span-2">
                            <Badge variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">
                              {ev.collection}
                            </Badge>
                          </div>
                          <div className="col-span-2 text-right tabular-nums">{ev.checked}</div>
                          <div className="col-span-2 text-right tabular-nums">
                            <span className={ev.updated > 0 ? "text-amber-800 font-medium" : "text-slate-500"}>{ev.updated}</span>
                          </div>
                          <div className="col-span-3 text-xs text-slate-600">
                            {flipped.length === 0 ? "All OK" : `${flipped.length} not active`}
                          </div>
                        </summary>
                        {flipped.length > 0 && (
                          <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 text-xs" data-testid={`link-health-event-${i}-flipped`}>
                            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                              URLs not returning 2xx
                            </div>
                            <ul className="space-y-1">
                              {flipped.slice(0, 30).map((it, j) => (
                                <li key={j} className="flex flex-wrap items-start gap-2">
                                  <Badge variant="outline" className={`rounded-none font-mono-tech text-[10px] ${
                                    it.new_status === "broken"
                                      ? "border-red-300 bg-red-50 text-red-800"
                                      : "border-amber-300 bg-amber-50 text-amber-800"
                                  }`}>
                                    {it.new_status}
                                    {it.http_code ? ` ${it.http_code}` : ""}
                                    {it.error ? ` · ${it.error}` : ""}
                                  </Badge>
                                  <span className="font-medium text-slate-800">{it.key}</span>
                                  <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 underline underline-offset-4 truncate max-w-[520px]">
                                    {it.url}
                                  </a>
                                </li>
                              ))}
                              {flipped.length > 30 && (
                                <li className="text-slate-500">… and {flipped.length - 30} more</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </details>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Account deletion audits */}
            <AuditSection
              id="admin-deletion-audits-section"
              testid="admin-deletion-audits-section"
              title="Account deletions"
              fetchPath="/admin/audits/account-deletions"
              emptyMessage="No account deletion requests recorded."
              columns={[
                { key: "at", label: "When", render: (v) => new Date(v).toLocaleString() },
                { key: "email", label: "Email" },
                { key: "action", label: "Action", render: (v) => <Badge variant="outline" className={`rounded-none font-mono-tech text-[10px] ${v === "requested" ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>{v}</Badge> },
                { key: "scheduled_at", label: "Erasure scheduled", render: (v) => v ? new Date(v).toLocaleDateString() : "—" },
                { key: "ip", label: "IP", render: (v) => <span className="font-mono-tech text-[11px] text-slate-500">{v || "—"}</span> },
              ]}
            />

            {/* Retention reminder audits */}
            <AuditSection
              id="admin-retention-audits-section"
              testid="admin-retention-audits-section"
              title="Retention reminders shown"
              fetchPath="/admin/audits/retention-reminders"
              emptyMessage="No retention reminders shown yet — no users have crossed day 21 or day 28."
              summaryKey="by_tier"
              summaryLabel="Total impressions by tier"
              columns={[
                { key: "shown_at", label: "When", render: (v) => new Date(v).toLocaleString() },
                { key: "email", label: "Email" },
                { key: "tier", label: "Tier", render: (v) => <Badge variant="outline" className={`rounded-none font-mono-tech text-[10px] ${v >= 28 ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>Day {v}</Badge> },
              ]}
            />
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function AuditSection({ id, testid, title, fetchPath, emptyMessage, columns, summaryKey, summaryLabel }) {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(fetchPath);
        setData(r.data);
      } catch (e) {
        toast.error(`Could not load ${title.toLowerCase()}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPath]);  // eslint-disable-line react-hooks/exhaustive-deps

  const items = data.items || [];
  const summary = summaryKey ? data[summaryKey] : null;

  return (
    <section id={id} className="mt-14" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-serif-display text-2xl">
          {title} <span className="label-tiny ml-2">{data.count || 0}</span>
        </h2>
        {summary && Object.keys(summary).length > 0 && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="label-tiny">{summaryLabel}:</span>
            {Object.entries(summary).map(([k, v]) => (
              <Badge key={k} variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">
                Day {k} · {v}
              </Badge>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        <div className="border border-slate-200 p-6 label-tiny">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-6 text-slate-500 text-sm">{emptyMessage}</div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-4 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-slate-600">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 200).map((row, i) => (
                <tr key={i} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50" data-testid={`${testid}-row-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2 text-slate-800 align-top">
                      {c.render ? c.render(row[c.key]) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 200 && (
            <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-200">
              Showing 200 of {items.length}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
